import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit, Trash2, Eye, Loader2, Lock, Unlock, 
  Calendar, Ticket, Clock, Building2, AlertTriangle,
  Settings, Users, Search, CheckCircle, XCircle, FileEdit, Link2,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { eventsApi } from '../../lib/api';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

// ============== DESIGN TOKENS ==============
const TABS = [
  { id: 'all', label: 'Todos' },
  { id: 'pending_approval', label: 'Pendientes', dot: true },
  { id: 'published', label: 'Publicados' },
  { id: 'completed', label: 'Ya Realizados' },
  { id: 'draft', label: 'Borradores' },
  { id: 'cancelled', label: 'Cancelados' },
];

const STATUS_CONFIG = {
  draft: { 
    label: 'Borrador', 
    bg: 'bg-zinc-500/10', 
    text: 'text-zinc-400',
    border: 'border-zinc-500/20'
  },
  pending_approval: { 
    label: 'Pendiente', 
    bg: 'bg-amber-500/10', 
    text: 'text-amber-400',
    border: 'border-amber-500/20',
    pulse: true
  },
  published: { 
    label: 'Publicado', 
    bg: 'bg-emerald-500/10', 
    text: 'text-emerald-400',
    border: 'border-emerald-500/20'
  },
  private: { 
    label: 'Privado', 
    bg: 'bg-purple-500/10', 
    text: 'text-purple-400',
    border: 'border-purple-500/20'
  },
  completed: { 
    label: 'Ya Realizado', 
    bg: 'bg-blue-500/10', 
    text: 'text-blue-400',
    border: 'border-blue-500/20'
  },
  cancelled: { 
    label: 'Cancelado', 
    bg: 'bg-red-500/10', 
    text: 'text-red-400',
    border: 'border-red-500/20'
  },
};

// ============== SUB-COMPONENTS ==============

const StatCard = ({ icon: Icon, label, value, variant, onClick }) => {
  const isPending = variant === 'pending';
  return (
    <div 
      onClick={onClick}
      className={`
        bg-[#121212] rounded-xl p-5 border transition-all cursor-pointer group
        ${isPending 
          ? 'border-amber-500/30 hover:border-amber-500/50 hover:shadow-[0_0_30px_rgba(245,158,11,0.1)]' 
          : 'border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900'
        }
      `}
      data-testid={`stat-card-${variant || 'default'}`}
    >
      <div className="flex items-start justify-between">
        <div className={`
          w-10 h-10 rounded-lg flex items-center justify-center
          ${isPending ? 'bg-amber-500/20' : 'bg-zinc-800'}
        `}>
          <Icon size={20} className={isPending ? 'text-amber-400' : 'text-zinc-400'} />
        </div>
        {isPending && value > 0 && (
          <span className="flex h-3 w-3">
            <span className="animate-ping absolute h-3 w-3 rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative rounded-full h-3 w-3 bg-amber-500"></span>
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-xs text-zinc-500 uppercase tracking-widest font-medium">{label}</p>
        <p className={`text-3xl font-bold font-mono mt-1 ${isPending && value > 0 ? 'text-amber-400' : 'text-white'}`}>
          {value}
        </p>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`
      inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border
      ${config.bg} ${config.text} ${config.border}
    `}>
      {config.pulse && (
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {config.label}
    </span>
  );
};

const EventRow = ({ event, onApprove, onReject, onSettings, onUnlock, onLock, onView, onEdit, onDelete, onRepublish, onChangeStatus, onToggleForceActive, canEdit, canDelete, isLocked }) => {
  const isPending = event.status === 'pending_approval';
  const isPublished = event.status === 'published';
  const isCompleted = event.status === 'completed';
  
  return (
    <tr 
      className="group border-b border-zinc-800/50 hover:bg-zinc-900/50 transition-colors cursor-pointer"
      data-testid={`event-row-${event.id}`}
      onClick={(e) => {
        // Don't navigate if clicking on buttons
        if (e.target.closest('button')) return;
        onView(event);
      }}
    >
      {/* Event Info */}
      <td className="py-3 px-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex-shrink-0 overflow-hidden rounded-lg bg-zinc-800">
            <img
              src={event.image_url || 'https://images.unsplash.com/photo-1682343712259-d3f5fa95ee22?w=200'}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="min-w-0 max-w-[180px]">
            <p className="font-bold text-white truncate text-sm flex items-center gap-1">
              {event.title}
              {isLocked && <Lock size={10} className="text-red-400 flex-shrink-0" />}
            </p>
            <p className="text-xs text-zinc-500 truncate">{event.location}</p>
          </div>
        </div>
      </td>
      
      {/* Producer - Always show producer name */}
      <td className="py-3 px-3">
        <div className="flex items-center gap-1.5 max-w-[120px]">
          <Building2 size={12} className="text-purple-400 flex-shrink-0" />
          <span className="text-xs text-zinc-300 truncate">
            {event.producer_name || 'Bravo Productora'}
          </span>
        </div>
      </td>
      
      {/* Date */}
      <td className="py-3 px-3">
        <span className="font-mono text-xs text-zinc-400 whitespace-nowrap">
          {formatInTimeZone(new Date(event.date), 'America/Argentina/Buenos_Aires', 'd MMM', { locale: es })}
        </span>
      </td>
      
      {/* Tickets */}
      <td className="py-3 px-3">
        <div className="flex items-baseline gap-0.5 font-mono text-xs whitespace-nowrap">
          <span className="text-[#D4AF37] font-bold">{event.tickets_sold || 0}</span>
          <span className="text-zinc-600">/</span>
          <span className="text-zinc-500">{event.ticket_quantity || 0}</span>
        </div>
      </td>
      
      {/* Status - Clickable select to change */}
      <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
        <Select 
          value={event.status} 
          onValueChange={(value) => onChangeStatus(event, value)}
        >
          <SelectTrigger className="w-[140px] h-8 bg-transparent border-none focus:ring-0 focus:ring-offset-0">
            <StatusBadge status={event.status} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700">
            <SelectItem value="published" className="text-green-400 focus:bg-green-500/10 focus:text-green-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                Publicado
              </div>
            </SelectItem>
            <SelectItem value="draft" className="text-zinc-400 focus:bg-zinc-800 focus:text-zinc-300">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-zinc-500" />
                Borrador
              </div>
            </SelectItem>
            <SelectItem value="completed" className="text-blue-400 focus:bg-blue-500/10 focus:text-blue-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                Ya Realizado
              </div>
            </SelectItem>
            <SelectItem value="cancelled" className="text-red-400 focus:bg-red-500/10 focus:text-red-400">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-red-500" />
                Cancelado
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </td>
      
      {/* Actions */}
      <td className="py-3 px-3">
        <div className="flex items-center justify-end gap-0.5 flex-nowrap" onClick={(e) => e.stopPropagation()}>
          {/* Primary Actions for Pending */}
          {isPending && (
            <div className="flex gap-1 mr-1">
              <Button
                size="sm"
                onClick={() => onApprove(event)}
                className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                data-testid="btn-approve"
              >
                <CheckCircle size={12} className="mr-1" />
                Aprobar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReject(event)}
                className="h-7 px-2 border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                data-testid="btn-reject"
              >
                <XCircle size={12} className="mr-1" />
                Rechazar
              </Button>
            </div>
          )}
          
          {/* Republish button for completed events */}
          {isCompleted && (
            <Button
              size="sm"
              onClick={() => onRepublish(event)}
              className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs mr-1"
              data-testid="btn-republish"
            >
              <CheckCircle size={12} className="mr-1" />
              Volver a Publicar
            </Button>
          )}
          
          {/* Force Active toggle for events that are past but need to stay visible */}
          {(isPublished || isCompleted) && (
            <Button
              size="sm"
              onClick={() => onToggleForceActive(event)}
              className={`h-7 px-2 text-xs mr-1 ${
                event.force_active 
                  ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                  : 'bg-zinc-700 hover:bg-zinc-600 text-zinc-300'
              }`}
              title={event.force_active ? "Desactivar forzado" : "Forzar como activo (ignora fecha)"}
              data-testid="btn-force-active"
            >
              <RefreshCw size={12} className={`mr-1 ${event.force_active ? 'animate-spin' : ''}`} />
              {event.force_active ? 'Forzado' : 'Forzar'}
            </Button>
          )}
          
          {/* Secondary Actions - Always visible */}
          <div className="flex gap-0">
            {/* Lock/Unlock */}
            {isLocked ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onUnlock(event)}
                className="h-7 w-7 p-0 text-amber-400 hover:bg-amber-500/10"
                title="Desbloquear evento"
              >
                <Unlock size={14} />
              </Button>
            ) : (isPublished || event.status === 'draft') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onLock(event)}
                className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                title="Dar de baja / Bloquear"
              >
                <Lock size={14} />
              </Button>
            )}
            
            {/* Settings */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSettings(event)}
              className="h-7 w-7 p-0 hover:bg-zinc-800 hover:text-[#D4AF37]"
              title="Configurar comisiones"
            >
              <Settings size={14} />
            </Button>
            
            {/* View/Edit - Goes to detail page */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onView(event)}
              className="h-7 w-7 p-0 hover:bg-zinc-800"
              title="Ver detalles"
            >
              <Eye size={14} />
            </Button>
            
            {/* Edit */}
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(event)}
                className="h-7 w-7 p-0 hover:bg-zinc-800 hover:text-[#D4AF37]"
                title="Editar evento"
              >
                <FileEdit size={14} />
              </Button>
            )}
            
            {/* Delete */}
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(event)}
                className="h-7 w-7 p-0 text-red-500 hover:bg-red-500/10"
                title="Eliminar"
              >
                <Trash2 size={14} />
              </Button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

// ============== MAIN COMPONENT ==============
const EventsManagerPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  // Modal states
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [republishModalOpen, setRepublishModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [eventSettings, setEventSettings] = useState({
    service_fee: 0,
    service_fee_type: 'fixed',
    free_service_for_bravx: true
  });
  const [saving, setSaving] = useState(false);
  const [migratingSlug, setMigratingSlug] = useState(false);

  const fetchEvents = async () => {
    try {
      const response = await eventsApi.getAll();
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Filter events
  const filteredEvents = events.filter(event => {
    const matchesTab = activeTab === 'all' || event.status === activeTab;
    const matchesSearch = !searchQuery || 
      event.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.producer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  // Stats
  const stats = {
    pending: events.filter(e => e.status === 'pending_approval').length,
    published: events.filter(e => e.status === 'published').length,
    producers: events.filter(e => e.producer_id).length,
    sold: events.reduce((acc, e) => acc + (e.tickets_sold || 0), 0)
  };

  // Handlers
  const handleApprove = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      await api.post(`/events/${selectedEvent.id}/approve`);
      toast.success('Evento aprobado y publicado');
      setApproveModalOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedEvent || !rejectReason.trim()) {
      toast.error('Ingresa un motivo de rechazo');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/events/${selectedEvent.id}/reject`, { reason: rejectReason });
      toast.success('Evento rechazado');
      setRejectModalOpen(false);
      setRejectReason('');
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      await api.put(`/events/${selectedEvent.id}`, eventSettings);
      toast.success('Configuración guardada');
      setSettingsModalOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      await api.post(`/events/${selectedEvent.id}/unlock`);
      toast.success('Evento desbloqueado');
      setUnlockModalOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desbloquear');
    } finally {
      setSaving(false);
    }
  };

  const handleLock = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      await api.post(`/events/${selectedEvent.id}/lock`);
      toast.success('Evento dado de baja');
      setLockModalOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al bloquear');
    } finally {
      setSaving(false);
    }
  };

  const handleRepublish = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      await api.put(`/events/${selectedEvent.id}`, { status: 'published' });
      toast.success('Evento publicado nuevamente');
      setRepublishModalOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al republicar');
    } finally {
      setSaving(false);
    }
  };

  const handleChangeStatus = async (event, newStatus) => {
    try {
      await api.put(`/events/${event.id}`, { status: newStatus });
      const statusLabels = {
        published: 'Publicado',
        draft: 'Borrador',
        completed: 'Ya Realizado',
        cancelled: 'Cancelado'
      };
      toast.success(`Estado cambiado a ${statusLabels[newStatus] || newStatus}`);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleToggleForceActive = async (event) => {
    try {
      const newValue = !event.force_active;
      await api.put(`/events/${event.id}`, { force_active: newValue });
      toast.success(newValue 
        ? 'Evento forzado como activo - Ignorará la fecha de finalización' 
        : 'Forzado desactivado - Se respetará la fecha del evento'
      );
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar configuración');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    try {
      await eventsApi.delete(selectedEvent.id);
      toast.success('Evento eliminado');
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const openSettingsModal = (event) => {
    setSelectedEvent(event);
    setEventSettings({
      service_fee: event.service_fee || 0,
      service_fee_type: event.service_fee_type || 'fixed',
      free_service_for_bravx: event.free_service_for_bravx !== false
    });
    setSettingsModalOpen(true);
  };

  const handleMigrateSlugs = async () => {
    setMigratingSlug(true);
    try {
      const response = await api.post('/events/migrate-slugs');
      const { migrated_count, events: migratedEvents } = response.data;
      
      if (migrated_count > 0) {
        toast.success(`¡Migración completada! Se generaron ${migrated_count} slugs`, {
          description: migratedEvents.slice(0, 3).map(e => `${e.title} → ${e.slug}`).join(', ') + (migrated_count > 3 ? '...' : '')
        });
        fetchEvents(); // Refresh to show new slugs
      } else {
        toast.info('Todos los eventos ya tienen slug asignado');
      }
    } catch (error) {
      console.error('Error migrating slugs:', error);
      toast.error(error.response?.data?.detail || 'Error al migrar slugs');
    } finally {
      setMigratingSlug(false);
    }
  };

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6" data-testid="events-manager">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-black tracking-tighter uppercase text-white">
                Eventos
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                Gestiona y aprueba eventos de productoras
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Migrate Slugs Button */}
              {hasPermission('events.edit') && (
                <Button 
                  onClick={handleMigrateSlugs}
                  disabled={migratingSlug}
                  variant="outline"
                  className="border-zinc-700 hover:bg-zinc-800 text-zinc-300"
                  title="Generar URLs amigables para eventos sin slug"
                >
                  {migratingSlug ? (
                    <Loader2 size={18} className="mr-2 animate-spin" />
                  ) : (
                    <Link2 size={18} className="mr-2" />
                  )}
                  Migrar Slugs
                </Button>
              )}
              {hasPermission('events.create') && (
                <Button 
                  onClick={() => navigate('/admin/eventos/crear')}
                  className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold"
                >
                  <Plus size={18} className="mr-2" />
                  Nuevo Evento
                </Button>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              icon={Clock} 
              label="Pendientes" 
              value={stats.pending}
              variant="pending"
              onClick={() => setActiveTab('pending_approval')}
            />
            <StatCard 
              icon={Eye} 
              label="Publicados" 
              value={stats.published}
              onClick={() => setActiveTab('published')}
            />
            <StatCard 
              icon={Building2} 
              label="De Productoras" 
              value={stats.producers}
            />
            <StatCard 
              icon={Ticket} 
              label="Entradas Vendidas" 
              value={stats.sold}
            />
          </div>

          {/* Control Bar */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-2 bg-[#121212] rounded-xl border border-zinc-800">
            {/* Tabs */}
            <div className="flex p-1 bg-zinc-900/80 rounded-lg overflow-x-auto scrollbar-hide">
              {TABS.map((tab) => {
                const count = tab.id === 'all' ? events.length : 
                  events.filter(e => e.status === tab.id).length;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      relative px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap flex-shrink-0
                      ${isActive 
                        ? 'bg-zinc-800 text-white shadow-sm' 
                        : 'text-zinc-500 hover:text-zinc-300'
                      }
                    `}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.dot && count > 0 && (
                      <span className="w-2 h-2 rounded-full bg-amber-400" />
                    )}
                    {tab.label}
                    <span className={`text-xs px-1.5 py-0.5 rounded ${isActive ? 'bg-zinc-700' : 'bg-zinc-800'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por nombre o productora..."
                className="pl-10 bg-zinc-900 border-transparent focus:border-zinc-700 w-full"
                data-testid="search-input"
              />
            </div>
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
            >
              {loading ? (
                <div className="flex items-center justify-center py-20 bg-[#121212] rounded-xl border border-zinc-800">
                  <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="bg-[#121212] border border-zinc-800 rounded-xl text-center py-16">
                  <Calendar size={48} className="mx-auto mb-4 text-zinc-700" />
                  <h3 className="text-xl font-bold text-white mb-2">Sin eventos</h3>
                  <p className="text-zinc-500">
                    {activeTab !== 'all' 
                      ? `No hay eventos ${TABS.find(t => t.id === activeTab)?.label.toLowerCase()}`
                      : 'Crea tu primer evento'
                    }
                  </p>
                </div>
              ) : (
                <div className="bg-[#121212] border border-zinc-800 rounded-xl overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left py-2 px-3 text-xs uppercase tracking-widest text-zinc-500 font-medium">Evento</th>
                        <th className="text-left py-2 px-3 text-xs uppercase tracking-widest text-zinc-500 font-medium">Productora</th>
                        <th className="text-left py-2 px-3 text-xs uppercase tracking-widest text-zinc-500 font-medium whitespace-nowrap">Fecha</th>
                        <th className="text-left py-2 px-3 text-xs uppercase tracking-widest text-zinc-500 font-medium whitespace-nowrap">Tickets</th>
                        <th className="text-left py-2 px-3 text-xs uppercase tracking-widest text-zinc-500 font-medium">Estado</th>
                        <th className="text-right py-2 px-3 text-xs uppercase tracking-widest text-zinc-500 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEvents.map((event) => (
                        <EventRow
                          key={event.id}
                          event={event}
                          isLocked={event.is_locked || event.status === 'cancelled'}
                          canEdit={hasPermission('events.edit')}
                          canDelete={hasPermission('events.delete')}
                          onApprove={(e) => { setSelectedEvent(e); setApproveModalOpen(true); }}
                          onReject={(e) => { setSelectedEvent(e); setRejectModalOpen(true); }}
                          onSettings={openSettingsModal}
                          onUnlock={(e) => { setSelectedEvent(e); setUnlockModalOpen(true); }}
                          onLock={(e) => { setSelectedEvent(e); setLockModalOpen(true); }}
                          onRepublish={(e) => { setSelectedEvent(e); setRepublishModalOpen(true); }}
                          onChangeStatus={handleChangeStatus}
                          onToggleForceActive={handleToggleForceActive}
                          onView={(e) => navigate(`/admin/eventos/editar/${e.id}`)}
                          onEdit={(e) => navigate(`/admin/eventos/editar/${e.id}`)}
                          onDelete={(e) => { setSelectedEvent(e); setDeleteDialogOpen(true); }}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ============== MODALS ============== */}
        
        {/* Approve Modal */}
        <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="text-emerald-400" size={20} />
                </div>
                Aprobar Evento
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-zinc-300">
                ¿Aprobar y publicar <strong className="text-white">{selectedEvent?.title}</strong>?
              </p>
              
              {selectedEvent?.producer_name && (
                <div className="bg-zinc-900 rounded-lg p-3 flex items-center gap-3">
                  <Building2 size={18} className="text-purple-400" />
                  <div>
                    <p className="text-xs text-zinc-500">Productora</p>
                    <p className="text-white font-medium">{selectedEvent.producer_name}</p>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setApproveModalOpen(false)} className="hover:bg-zinc-800">
                Cancelar
              </Button>
              <Button 
                onClick={handleApprove} 
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                Aprobar y Publicar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <XCircle className="text-red-400" size={20} />
                </div>
                Rechazar Evento
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-zinc-300">
                Rechazar <strong className="text-white">{selectedEvent?.title}</strong>
              </p>
              
              <div>
                <Label className="text-zinc-400 text-sm">Motivo del rechazo</Label>
                <Input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ej: Falta información del evento..."
                  className="bg-zinc-900 border-zinc-800 mt-2"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Se notificará a la productora
                </p>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setRejectModalOpen(false)} className="hover:bg-zinc-800">
                Cancelar
              </Button>
              <Button 
                onClick={handleReject} 
                disabled={saving || !rejectReason.trim()}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                Rechazar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Settings Modal */}
        <Dialog open={settingsModalOpen} onOpenChange={setSettingsModalOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <Settings className="text-[#D4AF37]" size={20} />
                </div>
                Configuración
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-6">
              {/* Service Fee */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-white">Cargo por Servicio</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-zinc-500">Tipo</Label>
                    <select
                      value={eventSettings.service_fee_type}
                      onChange={(e) => setEventSettings(prev => ({ ...prev, service_fee_type: e.target.value }))}
                      className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm"
                    >
                      <option value="fixed">Valor Fijo ($)</option>
                      <option value="percentage">Porcentaje (%)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">
                      {eventSettings.service_fee_type === 'fixed' ? 'Monto' : 'Porcentaje'}
                    </Label>
                    <Input
                      type="number"
                      value={eventSettings.service_fee}
                      onChange={(e) => setEventSettings(prev => ({ ...prev, service_fee: parseFloat(e.target.value) || 0 }))}
                      className="bg-zinc-900 border-zinc-800 mt-1"
                      min={0}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setSettingsModalOpen(false)} className="hover:bg-zinc-800">
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveSettings} 
                disabled={saving}
                className="bg-[#D4AF37] hover:bg-[#B8962E] text-black"
              >
                {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unlock Modal */}
        <Dialog open={unlockModalOpen} onOpenChange={setUnlockModalOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Unlock className="text-amber-400" size={20} />
                </div>
                Desbloquear Evento
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-zinc-300">
                ¿Desbloquear <strong className="text-white">{selectedEvent?.title}</strong>?
              </p>
              
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 flex gap-3">
                <AlertTriangle className="text-amber-400 flex-shrink-0" size={20} />
                <p className="text-sm text-amber-400/90">
                  El evento volverá a borrador y la productora podrá modificarlo.
                </p>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setUnlockModalOpen(false)} className="hover:bg-zinc-800">
                Cancelar
              </Button>
              <Button 
                onClick={handleUnlock} 
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                Desbloquear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lock Modal */}
        <Dialog open={lockModalOpen} onOpenChange={setLockModalOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Lock className="text-red-400" size={20} />
                </div>
                Dar de Baja Evento
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-zinc-300">
                ¿Dar de baja <strong className="text-white">{selectedEvent?.title}</strong>?
              </p>
              
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex gap-3">
                <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
                <div className="text-sm text-red-400/90">
                  <p>El evento será bloqueado y dejará de ser visible públicamente.</p>
                  <p className="mt-1">La productora no podrá modificarlo hasta que lo desbloquees.</p>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setLockModalOpen(false)} className="hover:bg-zinc-800">
                Cancelar
              </Button>
              <Button 
                onClick={handleLock} 
                disabled={saving}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                Dar de Baja
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Republish Modal */}
        <Dialog open={republishModalOpen} onOpenChange={setRepublishModalOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-white flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="text-emerald-400" size={20} />
                </div>
                Volver a Publicar Evento
              </DialogTitle>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <p className="text-zinc-300">
                ¿Volver a publicar <strong className="text-white">{selectedEvent?.title}</strong>?
              </p>
              
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 flex gap-3">
                <CheckCircle className="text-emerald-400 flex-shrink-0" size={20} />
                <div className="text-sm text-emerald-400/90">
                  <p>El evento volverá a ser visible públicamente.</p>
                  <p className="mt-1">La productora podrá continuar gestionando entradas y lineup.</p>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setRepublishModalOpen(false)} className="hover:bg-zinc-800">
                Cancelar
              </Button>
              <Button 
                onClick={handleRepublish} 
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                Volver a Publicar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-zinc-950 border border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar evento?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Esta acción eliminará permanentemente el evento y todos sus datos asociados.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default EventsManagerPage;
